import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO_IMG =
  "https://images.unsplash.com/photo-1728279824341-68a9c2508e77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxmYXNoaW9uJTIwZWRpdG9yaWFsJTIwd29tYW4lMjBuYXR1cmV8ZW58MHx8fHwxNzc3NDQ4MjUxfDA&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
  {
    name: "Bolsos",
    img:
      "https://images.pexels.com/photos/12442673/pexels-photo-12442673.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Ropa",
    img:
      "https://images.pexels.com/photos/8484131/pexels-photo-8484131.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Accesorios",
    img:
      "https://images.pexels.com/photos/4741622/pexels-photo-4741622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((res) => setFeatured(res.data.slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-12 md:pt-16 pb-20">
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="order-2 md:order-1">
            <div className="label-tracked text-[#C27A62] mb-6">
              Moda y complementos · Handmade
            </div>
            <h1 className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1E1E1E]">
              Piezas únicas,
              <br />
              hechas con tiempo
              <br />
              y con alma.
            </h1>
            <p className="mt-8 text-[#5C5C5C] text-base leading-relaxed max-w-md">
              Diseño propio, materiales nobles y procesos lentos. Cada pieza de
              GALOLÀ nace en mi taller, pensada para acompañarte mucho tiempo.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/catalogo" className="btn-primary" data-testid="hero-cta-catalog">
                Explorar colección <ArrowRight size={14} />
              </Link>
              <Link to="/sobre" className="btn-secondary" data-testid="hero-cta-about">
                Sobre GALOLÀ
              </Link>
            </div>
          </div>
          <div className="order-1 md:order-2 relative">
            <div className="aspect-[4/5] overflow-hidden bg-[#EDE8DD]">
              <img
                src={HERO_IMG}
                alt="GALOLÀ editorial"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hidden md:block absolute -bottom-10 -left-10 bg-[#F9F6F0] py-5 px-6 border border-[#E5E0D8] max-w-[240px]">
              <div className="font-serif-editorial italic text-lg leading-snug text-[#1E1E1E]">
                "Cada puntada cuenta una historia."
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-tracked text-[#C27A62] mb-2">Explora</div>
            <h2 className="font-serif-editorial text-3xl md:text-5xl text-[#1E1E1E]">
              Categorías
            </h2>
          </div>
          <Link
            to="/catalogo"
            className="hidden md:inline-flex label-tracked text-[#1E1E1E] hover:text-[#C27A62]"
          >
            Ver todo →
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {CATEGORIES.map((c) => (
            <Link
              key={c.name}
              to={`/catalogo?cat=${encodeURIComponent(c.name)}`}
              className="group relative block aspect-[3/4] overflow-hidden"
              data-testid={`category-${c.name.toLowerCase()}`}
            >
              <img
                src={c.img}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="label-tracked text-white/80 mb-1">
                  Categoría
                </div>
                <h3 className="font-serif-editorial text-3xl">{c.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-tracked text-[#C27A62] mb-2">Selección</div>
            <h2 className="font-serif-editorial text-3xl md:text-5xl text-[#1E1E1E]">
              Piezas destacadas
            </h2>
          </div>
        </div>
        {featured.length === 0 ? (
          <p className="text-[#5C5C5C]">Cargando colección...</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* BRAND STORY TEASER */}
      <section className="bg-[#FAF9F5] border-y border-[#E5E0D8] my-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-24 text-center">
          <div className="label-tracked text-[#C27A62] mb-4">Manifiesto</div>
          <p className="font-serif-editorial italic text-3xl md:text-4xl leading-snug text-[#1E1E1E]">
            "Creo piezas que no quieres soltar. Sin prisa, sin moldes, hechas a
            mano —una a una— para acompañarte de verdad."
          </p>
          <div className="mt-8">
            <Link to="/sobre" className="btn-secondary" data-testid="manifiesto-cta">
              Conocer mi historia
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
